<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { API_BASE_URL, isAllowedDownloadUrl, LANZOU_DOWNLOAD_URL, LANZOU_DOWNLOAD_PASSWORD } from '../app/config'
import { useSiteLocale } from '../composables/useSiteLocale'
import type { DownloadArtifact } from '../types'

const { t } = useI18n()
const { currentLocale } = useSiteLocale()

interface VersionResponse {
  version?: {
    latestVersionName?: string
    latestVersionCode?: number
    apkUrl?: string
    apkUrls?: string[]
    sha256?: string
  }
}

const versions = ref<Record<'stable' | 'beta', VersionResponse['version']>>({ stable: undefined, beta: undefined })

const loadVersion = async (channel: 'stable' | 'beta'): Promise<void> => {
  const appId = channel === 'beta' ? 'motexa-ride-beta' : 'motexa-ride'
  try {
    const response = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/version?appId=${appId}&versionCode=0`, {
      cache: 'no-store',
      headers: { accept: 'application/json' },
    })
    if (!response.ok) return
    const body = await response.json() as VersionResponse
    versions.value = { ...versions.value, [channel]: body.version }
  } catch {
    // A failed status request must leave the download action disabled.
  }
}

onMounted(() => void Promise.all([loadVersion('stable'), loadVersion('beta')]))

const artifact = (channel: 'stable' | 'beta'): DownloadArtifact => {
  const version = versions.value[channel]
  const candidates = [version?.apkUrl, ...(version?.apkUrls ?? [])]
  const downloadUrl = candidates.find((value): value is string => typeof value === 'string' && isAllowedDownloadUrl(value))
  return {
    channel,
    versionCode: version?.latestVersionCode,
    label: t(channel === 'stable' ? 'common.stable' : 'common.beta'),
    version: version?.latestVersionName || '—',
    publishDate: downloadUrl
      ? (currentLocale.value === 'zh-CN' ? '可下载' : 'Available')
      : (currentLocale.value === 'zh-CN' ? '请查看发布页' : 'See releases'),
    filename: downloadUrl ? new URL(downloadUrl).pathname.split('/').pop() || '' : '',
    sha256: version?.sha256,
    downloadUrl,
    available: downloadUrl != null,
  }
}

const artifacts = computed<DownloadArtifact[]>(() => [
  artifact('stable'),
  artifact('beta'),
])
</script>

<template>
  <div class="simple-page download-page">
    <header class="simple-page__hero">
      <span class="eyebrow">DOWNLOAD / MOTEXA RIDE</span>
      <h1>{{ currentLocale === 'zh-CN' ? '下载即刻上手' : 'Start riding with more confidence.' }}</h1>
    </header>
    <section class="download-grid">
      <article v-for="artifact in artifacts" :key="artifact.channel" class="download-card" :class="`download-card--${artifact.channel}`">
        <div class="download-card__top"><span>{{ artifact.label }}</span><b>{{ artifact.version }}</b></div>
        <div class="download-card__date">{{ currentLocale === 'zh-CN' ? '发布状态' : 'Release status' }} <strong>{{ artifact.publishDate }}</strong></div>
        <div class="download-card__mark">{{ artifact.channel === 'stable' ? '01' : 'β' }}</div>
        <h2>{{ artifact.channel === 'stable' ? (currentLocale === 'zh-CN' ? '稳定、清晰、随时可用。' : 'Stable, clear, ready.') : (currentLocale === 'zh-CN' ? '提前体验下一次变化。' : 'Preview what changes next.') }}</h2>
        <p>{{ artifact.channel === 'stable' ? (currentLocale === 'zh-CN' ? '适合日常连接车辆、实时骑行和保存记录。' : 'For everyday vehicle connections, live rides and saved records.') : (currentLocale === 'zh-CN' ? '测试版用于体验新功能和验证新设备。' : 'Test builds explore new features and device support.') }}</p>
        <div v-if="artifact.available" class="download-card__actions">
          <a class="button button--primary" :href="artifact.downloadUrl" data-site-event="apk_click" :data-site-channel="artifact.channel" :data-site-version="artifact.versionCode">
            {{ currentLocale === 'zh-CN' ? '服务器下载' : 'Direct download' }}<span aria-hidden="true">↗</span>
          </a>
          <a class="button download-card__mirror" :href="LANZOU_DOWNLOAD_URL" target="_blank" rel="noopener noreferrer" :aria-label="currentLocale === 'zh-CN' ? `蓝奏云下载，密码 ${LANZOU_DOWNLOAD_PASSWORD}，在新窗口打开` : `Lanzou download, password ${LANZOU_DOWNLOAD_PASSWORD}, opens in a new window`">
            {{ currentLocale === 'zh-CN' ? '蓝奏云下载' : 'Lanzou download' }}<span aria-hidden="true">↗</span>
          </a>
          <small class="download-card__mirror-note">{{ currentLocale === 'zh-CN' ? `蓝奏云密码：${LANZOU_DOWNLOAD_PASSWORD} · 请在目录中选择对应版本` : `Lanzou password: ${LANZOU_DOWNLOAD_PASSWORD} · Choose the matching version in the folder` }}</small>
        </div>
        <a v-else class="button button--primary" href="https://github.com/shj56166/Motexa/releases" target="_blank" rel="noopener noreferrer">{{ currentLocale === 'zh-CN' ? '查看 GitHub 发布页' : 'View GitHub releases' }}<span aria-hidden="true">↗</span></a>
        <small class="download-card__note">{{ artifact.sha256 ? `SHA-256 ${artifact.sha256}` : (currentLocale === 'zh-CN' ? '发布 APK 后，这里会显示校验信息。' : 'Checksum details will appear after release.') }}</small>
        <a v-if="artifact.available" class="download-card__releases" href="https://github.com/shj56166/Motexa/releases" target="_blank" rel="noopener noreferrer">{{ currentLocale === 'zh-CN' ? '查看 GitHub 发布记录 ↗' : 'View GitHub releases ↗' }}</a>
      </article>
    </section>
    <section class="download-safety"><span class="eyebrow">SAFETY NOTE</span><p>{{ currentLocale === 'zh-CN' ? 'Motexa Ride 是非官方硬件工具。请在停车、断载或低风险环境中使用进阶功能，不要在行驶过程中操作手机。' : 'Motexa Ride is an unofficial hardware utility. Use advanced functions while stationary or unloaded, and never operate the phone while riding.' }}</p></section>
  </div>
</template>

<style scoped>
.download-page { --download-ink: #10233f; --download-muted: #5b6d86; --download-blue: #1769e8; --download-soft: #edf5ff; min-height: calc(100vh - 170px); max-width: 1160px; padding-top: 88px; color: var(--download-ink); }
.download-page :deep(.eyebrow) { color: #377bdc; letter-spacing: .14em; }
.download-page .simple-page__hero { padding-bottom: 48px; }
.download-page .simple-page__hero h1 { color: var(--download-ink); font-size: clamp(48px, 7vw, 96px); letter-spacing: -.075em; margin-top: 20px; }
.download-page .simple-page__hero p { color: var(--download-muted); }
.download-grid { gap: 22px; }
.download-card { min-height: 470px; padding: clamp(24px, 4vw, 46px); border: 1px solid rgba(28, 91, 167, .14); border-radius: 32px; background: rgba(255,255,255,.78); box-shadow: 0 22px 70px rgba(47, 91, 146, .11); backdrop-filter: blur(24px); }
.download-card--stable { background: linear-gradient(145deg, #eaf4ff, #fff 68%); }
.download-card--beta { background: linear-gradient(145deg, #f3f7fc, #fff 68%); }
.download-card__top { color: #6e819b; }
.download-card__top span { color: #1b5fc4; font-size: 15px; font-weight: 700; letter-spacing: .02em; text-transform: none; }
.download-card__top b { color: var(--download-ink); }
.download-card__date { margin-top: 12px; color: #71839b; font-size: 11px; }
.download-card__date strong { margin-left: 6px; color: #315781; font-weight: 600; }
.download-card__mark { color: var(--download-blue); }
.download-card h2 { color: var(--download-ink); }
.download-card p { color: var(--download-muted); }
.download-card .button--primary { background: var(--download-blue); color: #fff; border-radius: 999px; box-shadow: 0 10px 24px rgba(23, 105, 232, .22); }
.download-card__actions { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; }
.download-card__actions .button { min-height: 44px; }
.download-card__mirror { color: var(--download-blue); background: #edf5ff; border: 1px solid rgba(23, 105, 232, .2); border-radius: 999px; }
.download-card__mirror:hover { background: #e1efff; }
.download-card__mirror-note { flex-basis: 100%; color: var(--download-muted); line-height: 1.7; }
.download-card .button--disabled { color: #8b9bb0; background: #e8eef6; border-radius: 999px; }
.download-card__note { overflow-wrap: anywhere; color: #8292a6; }
.download-card__releases { margin-top: 12px; color: var(--download-blue); font-size: 12px; }
.download-safety { border-top-color: rgba(28, 91, 167, .14); }
.download-safety p { color: var(--download-muted); }
@media (max-width: 700px) { .download-page { padding-top: 86px; } .download-card { border-radius: 27px; } }
</style>
